import { Permission } from '@today/types'
import type { AppSidebarGroup, NavigationPredicate } from '@today/ui-components'

/**
 * Platform Console (AD-4) sidebar navigation — the sections and order defined by
 * `docs/product/ui-specs/platform-console/inventory.md`. Each item self-gates on
 * the read permission atom the inventory assigns it (ADR-0063: gating is on the
 * atom, never a role), so a read-mostly PLATFORM_ADMIN sees only the sections it
 * may read and the platform-owner-only sections are absent from its DOM.
 *
 * The groups are consumed by `AppSidebar` (TO-633); the predicate names map to
 * {@link platformNavPredicates}.
 */
export const PLATFORM_NAV_GROUPS: AppSidebarGroup[] = [
	{
		key: 'overview',
		items: [{ label: 'Overview', target: '/admin', visibleIf: 'can-read-platform' }],
	},
	{
		key: 'partners',
		label: 'Partners',
		items: [{ label: 'Tenants', target: '/admin/tenants', visibleIf: 'can-read-tenants' }],
	},
	{
		key: 'brand',
		label: 'Brand',
		items: [
			{ label: 'Brand catalog', target: '/admin/brands', visibleIf: 'can-read-brand-catalog' },
			// The platform brand POINTER is the platform-wide authority — SUPER_ADMIN only.
			{ label: 'Platform brand', target: '/admin/platform/brand', visibleIf: 'is-super-admin' },
		],
	},
	{
		key: 'providers-ai',
		label: 'Providers & AI',
		items: [
			{ label: 'Providers', target: '/admin/providers', visibleIf: 'is-super-admin' },
			{ label: 'AI', target: '/admin/ai', visibleIf: 'is-super-admin' },
		],
	},
	{
		key: 'support',
		label: 'Support',
		items: [
			{ label: 'Support sessions', target: '/admin/support-sessions', visibleIf: 'is-super-admin' },
			{ label: 'Audit', target: '/admin/audit', visibleIf: 'can-read-platform-audit' },
			{ label: 'Realtime', target: '/admin/realtime', visibleIf: 'is-super-admin' },
		],
	},
]

/** True when the viewer holds any of the given atoms. */
function holdsAny(permissions: readonly string[], ...atoms: Permission[]): boolean {
	return atoms.some((atom) => permissions.includes(atom))
}

/**
 * The platform plane's SUPER_ADMIN sentinel.
 *
 * The platform-owner-only sections (Platform brand pointer, Providers, AI,
 * Support sessions, Realtime) have no PLATFORM_ADMIN-excluded *read* atom to key
 * on — they are greenfield SUPER_ADMIN surfaces whose read atoms don't exist yet
 * (platform-plane-shell design D3). We gate them on `tenant.manage`, which only
 * `ROLE_PERMISSIONS[SUPER_ADMIN]` (= every atom) holds and `PLATFORM_ADMIN` does
 * not. Keying on this atom keeps the nav renderer's permissions-only contract
 * intact (ADR-0063 §2 / no-direct-role-check) — no role literal is compared here.
 *
 * INVARIANT: `tenant.manage` must stay absent from the PLATFORM_ADMIN bundle; a
 * unit test asserts this, so a future grant fails loudly rather than silently
 * exposing the owner-only nav. When a dedicated read atom is later minted for one
 * of these surfaces, move its nav item from `is-super-admin` to that atom.
 */
function isSuperAdmin(permissions: readonly string[]): boolean {
	return permissions.includes(Permission.TENANT_MANAGE)
}

/**
 * Closed-set predicate map for {@link PLATFORM_NAV_GROUPS}, resolved by
 * `AppSidebar`. Each predicate decides visibility from the viewer's permission
 * atoms alone.
 */
export const platformNavPredicates: Record<string, NavigationPredicate> = {
	// The platform plane entry: any platform operator who may read tenants or the
	// platform audit (SUPER_ADMIN + PLATFORM_ADMIN).
	'can-read-platform': (permissions) =>
		holdsAny(permissions, Permission.PLATFORM_TENANT_READ, Permission.PLATFORM_AUDIT),
	'can-read-tenants': (permissions) => holdsAny(permissions, Permission.PLATFORM_TENANT_READ),
	'can-read-brand-catalog': (permissions) => holdsAny(permissions, Permission.PLATFORM_BRAND_READ),
	// Gate on the PLATFORM-scoped audit atom only. `audit.read` alone is held by
	// tenant-plane roles (ORG_MANAGER/ORG_ADMIN) too; including it would surface the
	// platform Audit nav to a tenant admin who merely browsed to /admin. SUPER_ADMIN +
	// PLATFORM_ADMIN both hold `platform.audit`, so both still see it.
	'can-read-platform-audit': (permissions) => holdsAny(permissions, Permission.PLATFORM_AUDIT),
	'is-super-admin': isSuperAdmin,
}
